import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, MapPin, Send, Loader2, Star, MessageCircle, ChevronRight, Brain, Scale, ArrowRight, Clock, Video, Zap, Building2, TrendingUp, BadgeCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { CITIES, CATEGORIES } from '../mock/mock';
import { aiApi } from '../api/client';
import SEO from '../components/SEO';

const EXAMPLES = [
  'Fui demitido sem justa causa e a empresa não pagou minhas verbas rescisórias.',
  'Meu vizinho invadiu parte do meu terreno e não quer sair. O que posso fazer?',
  'Comprei um produto online que veio com defeito e a loja se recusa a trocar.',
  'Preciso abrir inventário após o falecimento do meu pai.',
  'Fui vítima de golpe pela internet e tive prejuizo financeiro grande.',
];

const FACTOR_LABELS = {
  especialidade: { label: 'Especialista na área', icon: Scale, color: 'text-[#075a6d]' },
  localizacao: { label: 'Localização ideal', icon: MapPin, color: 'text-blue-600' },
  online: { label: 'Atende online', icon: Video, color: 'text-purple-600' },
  online_disponivel: { label: 'Atende online', icon: Video, color: 'text-purple-600' },
  disponibilidade: { label: 'Alta disponibilidade', icon: Clock, color: 'text-emerald-600' },
  horario: { label: 'Horário compatível', icon: Clock, color: 'text-[#425a62]' },
  resposta_rapida: { label: 'Resposta rápida', icon: Zap, color: 'text-amber-600' },
  velocidade_resposta: { label: 'Resposta rápida', icon: Zap, color: 'text-amber-600' },
  avaliacao_alta: { label: 'Bem avaliado', icon: Star, color: 'text-[#075a6d]' },
  conversao: { label: 'Alta conversão', icon: TrendingUp, color: 'text-emerald-600' },
  tipo_cliente: { label: 'Perfil do cliente', icon: Building2, color: 'text-[#425a62]' },
  complexidade: { label: 'Complexidade adequada', icon: BadgeCheck, color: 'text-blue-600' },
};

function FactorBadge({ factor }) {
  const cfg = FACTOR_LABELS[factor] || FACTOR_LABELS.especialidade;
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1 bg-[#f2f6f7] rounded-full px-2 py-0.5 text-[10px] font-medium text-[#354c54]">
      <Icon className={`w-3 h-3 ${cfg.color}`} /> {cfg.label}
    </span>
  );
}

export default function AIMatch() {
  const [problem, setProblem] = useState('');
  const [city, setCity] = useState('');
  const [online, setOnline] = useState(false);
  const [urgency, setUrgency] = useState(false);
  const [clientType, setClientType] = useState('');
  const [showPrefs, setShowPrefs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (problem.trim().length < 10) return;
    setLoading(true); setResult(null);
    try {
      const data = await aiApi.match(problem, {
        city: city || null,
        online: online || null,
        urgency: urgency || null,
        clientType: clientType || null,
      });
      setResult(data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Erro ao processar. Tente novamente.' });
    } finally { setLoading(false); }
  };

  return (
    <div>
      <SEO title="Encontre um advogado com IA" description="Descreva seu problema e nossa IA jurídica encontra os 3 melhores advogados considerando 10 fatores: especialidade, localização, disponibilidade, tempo de resposta, avaliações e mais." />

      <section className="relative bg-white border-b border-[#cbd8dc] text-[#122f36] overflow-hidden">
        <div className="aj-wrap max-w-[820px] py-14 md:py-16 relative">
          <div className="inline-flex items-center gap-2 bg-[#edf4f5] border border-[#cbd8dc] rounded-full px-4 py-1.5 text-xs font-medium text-[#075a6d] mb-4">
            <Sparkles className="w-3.5 h-3.5" /> IA jurídica avançada • GPT-5 • 10 fatores de aderência
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold leading-tight">Descreva seu problema. <span className="text-[#075a6d]">Encontramos o advogado ideal.</span></h1>
          <p className="mt-4 text-lg text-[#354c54] max-w-2xl">Nossa IA analisa 10 critérios: especialidade, cidade, tipo de cliente, atendimento online, disponibilidade, horário, histórico de conversão, velocidade de resposta, avaliações e complexidade do caso.</p>
        </div>
      </section>

      <div className="aj-wrap max-w-[820px] mt-8 relative pb-16">
        <form onSubmit={submit} className="bg-white rounded-[10px] border border-[#cbd8dc] p-6 md:p-8">
          <label className="text-sm font-semibold text-[#153e44] mb-2 block">Conte-nos o que aconteceu</label>
          <Textarea value={problem} onChange={(e) => setProblem(e.target.value)} rows={6} placeholder="Ex: Trabalhei 3 anos em uma empresa que fechou de repente. Não me pagaram os últimos 2 meses de salário, FGTS nem multa rescisória..." className="resize-none" maxLength={2000} />
          <div className="text-xs text-[#52676e] mt-1">{problem.length}/2000 • Mínimo 10 caracteres</div>

          <div className="mt-4">
            <button type="button" onClick={() => setShowPrefs(!showPrefs)} className="text-xs font-semibold text-[#153e44] hover:text-[#063f4c] flex items-center gap-1">
              <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showPrefs ? 'rotate-90' : ''}`} /> {showPrefs ? 'Ocultar preferências avançadas' : 'Personalizar preferências (opcional)'}
            </button>
            {showPrefs && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-[#f7fafb] rounded-[7px]">
                <div>
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Cidade</label>
                  <Select value={city || 'all'} onValueChange={(v) => setCity(v === 'all' ? '' : v)}>
                    <SelectTrigger className="h-10"><div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#6b858e]" /><SelectValue placeholder="Qualquer" /></div></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Qualquer cidade</SelectItem>
                      {CITIES.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Tipo de cliente</label>
                  <Select value={clientType || 'any'} onValueChange={(v) => setClientType(v === 'any' ? '' : v)}>
                    <SelectTrigger className="h-10"><SelectValue placeholder="Qualquer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Qualquer</SelectItem>
                      <SelectItem value="Pessoa Física">Pessoa Física</SelectItem>
                      <SelectItem value="Pessoa Jurídica">Pessoa Jurídica</SelectItem>
                      <SelectItem value="Startups">Startup</SelectItem>
                      <SelectItem value="Servidores Públicos">Servidor Público</SelectItem>
                      <SelectItem value="Aposentados">Aposentado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-3 p-3 bg-white rounded-[7px] border border-[#cbd8dc] cursor-pointer">
                  <Video className="w-4 h-4 text-[#075a6d]" />
                  <div className="flex-1"><div className="text-sm font-medium text-[#153e44]">Prefiro atendimento online</div><div className="text-xs text-[#52676e]">Videochamada ou telefone</div></div>
                  <Switch checked={online} onCheckedChange={setOnline} />
                </label>
                <label className="flex items-center gap-3 p-3 bg-white rounded-[7px] border border-[#cbd8dc] cursor-pointer">
                  <Zap className="w-4 h-4 text-amber-600" />
                  <div className="flex-1"><div className="text-sm font-medium text-[#153e44]">Caso urgente</div><div className="text-xs text-[#52676e]">Prioriza resposta rápida e 24h</div></div>
                  <Switch checked={urgency} onCheckedChange={setUrgency} />
                </label>
              </div>
            )}
          </div>

          <div className="mt-5">
            <Button type="submit" disabled={loading || problem.trim().length < 10} className="w-full h-12 bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold text-base">
              {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analisando 10 fatores com IA...</>) : (<><Send className="w-5 h-5 mr-2" /> Encontrar advogados ideais</>)}
            </Button>
          </div>

          {!result && !loading && (
            <div className="mt-6 pt-6 border-t border-[#e1e9eb]">
              <p className="text-xs font-semibold text-[#52676e] uppercase tracking-wider mb-2">Exemplos</p>
              <div className="flex flex-wrap gap-2">
                {EXAMPLES.map((ex, i) => (
                  <button key={i} type="button" onClick={() => setProblem(ex)} className="text-xs px-3 py-1.5 rounded-full bg-[#f2f6f7] hover:bg-[#0c3036]/20 text-[#354c54] hover:text-[#153e44] transition-colors">
                    {ex.slice(0, 55)}...
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>

        {loading && (
          <div className="mt-8 text-center py-12">
            <div className="inline-flex items-center gap-3 text-[#153e44]">
              <Brain className="w-6 h-6 animate-pulse text-[#075a6d]" />
              <span className="font-medium">Cruzando 10 fatores para encontrar o melhor match...</span>
            </div>
          </div>
        )}

        {result?.error && <div className="mt-6 bg-red-50 border border-red-200 rounded-[7px] p-5 text-red-700 text-sm">{result.error}</div>}

        {result?.matches?.length > 0 && (
          <div className="mt-8 space-y-6">
            {result.summary && (
              <div className="aj-edition bg-[#f7fafb] rounded-r-[10px] p-6">
                <div className="flex items-center gap-2 text-[#075a6d] text-xs font-semibold uppercase tracking-widest mb-2"><Sparkles className="w-4 h-4" /> Análise da IA</div>
                <p className="leading-relaxed whitespace-pre-line">{result.summary}</p>
                <div className="mt-4 pt-4 border-t border-[#cbd8dc] flex flex-wrap items-center gap-3 text-sm">
                  {result.suggestedArea && (
                    <div className="flex items-center gap-2"><Scale className="w-4 h-4 text-[#075a6d]" /><span className="text-[#425a62]">Área:</span><Link to={`/categoria/${result.suggestedArea}`} className="text-[#075a6d] font-semibold hover:underline capitalize">{result.suggestedArea.replace('-', ' ')}</Link></div>
                  )}
                  {result.complexity && (
                    <div className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#075a6d]" /><span className="text-[#425a62]">Complexidade:</span><span className="text-[#122f36] font-semibold capitalize">{result.complexity}</span></div>
                  )}
                </div>
              </div>
            )}

            <h2 className="text-xl font-semibold text-[#153e44]">Os 3 escritórios mais adequados para você</h2>
            {result.matches.map((m, i) => {
              const f = m.firm;
              const whatsappUrl = `https://wa.me/${f.whatsapp}?text=${encodeURIComponent('Olá! A plataforma me recomendou seu escritório. Poderia me ajudar?')}`;
              return (
                <div key={f.id} className="bg-white rounded-[10px] border border-[#cbd8dc] p-5 md:p-6 hover:border-[#547a83] transition-shadow">
                  <div className="flex gap-4">
                    <div className="relative shrink-0">
                      <img src={f.photo} alt={f.leadLawyer} className="w-20 h-20 md:w-24 md:h-24 rounded-[7px] object-cover" />
                      <div className="absolute -top-2 -left-2 w-8 h-8 rounded-full bg-[#153e44] text-white font-semibold text-sm flex items-center justify-center shadow">{i + 1}º</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div>
                          <h3 className="font-semibold text-[#153e44] text-lg">{f.name}</h3>
                          <p className="text-xs text-[#52676e]">{f.leadLawyer} • {f.oab}</p>
                        </div>
                        {m.score > 0 && (
                          <div className="text-right shrink-0">
                            <div className="text-xs text-[#52676e]">Aderência</div>
                            <div className="text-lg font-semibold text-[#075a6d]">{m.score}%</div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[#425a62]">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-[#075a6d]" /> {f.cityName}/{f.state}</span>
                        <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-[#153e44] text-[#153e44]" /> {f.rating.toFixed(1)} ({f.reviews})</span>
                        {f.avgResponseHours != null && <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-amber-600" /> Responde em ~{f.avgResponseHours}h</span>}
                        {f.onlineService && <span className="flex items-center gap-1"><Video className="w-3 h-3 text-purple-600" /> Online</span>}
                        {f.availability24h && <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-emerald-600" /> 24h</span>}
                      </div>
                      {m.factors?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {m.factors.map((factor, j) => <FactorBadge key={j} factor={factor} />)}
                        </div>
                      )}
                      <div className="mt-3 bg-[#edf4f5] border border-[#2f5d65]/20 rounded-[7px] p-3">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-[#075a6d] mt-0.5 shrink-0" />
                          <p className="text-sm text-[#354c54]"><span className="font-semibold text-[#153e44]">Por que este escritório: </span>{m.reason}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#e1e9eb]">
                    <Link to={`/escritorio/${f.slug}`} className="flex-1 min-w-[140px]">
                      <Button variant="outline" className="w-full">Ver perfil completo <ArrowRight className="w-4 h-4 ml-2" /></Button>
                    </Link>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-[#25D366] hover:bg-[#1fb958]"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</Button>
                    </a>
                  </div>
                </div>
              );
            })}
            <div className="text-center pt-4">
              <Button variant="outline" onClick={() => { setResult(null); setProblem(''); }}>Fazer nova consulta</Button>
            </div>
          </div>
        )}

        {!result && !loading && (
          <div className="mt-12">
            <h2 className="text-center text-2xl font-semibold text-[#153e44] mb-2">Nossa IA analisa 10 fatores</h2>
            <p className="text-center text-[#425a62] mb-8">Não é só especialidade. Nossa IA cruza tudo o que importa para o seu caso.</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { icon: Scale, label: 'Especialidade' },
                { icon: MapPin, label: 'Localização' },
                { icon: Building2, label: 'Tipo de cliente' },
                { icon: Video, label: 'Atende online' },
                { icon: Clock, label: 'Disponibilidade' },
                { icon: Clock, label: 'Horário' },
                { icon: TrendingUp, label: 'Conversão' },
                { icon: Zap, label: 'Resposta rápida' },
                { icon: Star, label: 'Avaliações' },
                { icon: BadgeCheck, label: 'Complexidade' },
              ].map((f, i) => (
                <div key={i} className="bg-white border border-[#cbd8dc] rounded-[7px] p-3 text-center hover:border-[#547a83] transition-colors">
                  <div className="w-9 h-9 rounded-[7px] bg-[#153e44] flex items-center justify-center mx-auto mb-2"><f.icon className="w-4 h-4 text-[#075a6d]" /></div>
                  <div className="text-xs font-semibold text-[#153e44]">{f.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
